<script>

/**
 * @param manifest { Object }
 * @property manifest.id { Number } マニフェストID
 * @property manifest.enable_copy { Boolean } 複写ボタン表示対象
 * @property manifest.manifest_type { String } マニフェスト種別
 * @property manifest.report_reporter_name { String } 報告担当者
 * @property manifest.report_transport_completed_date { String } 運搬完了日
 * @property manifest.report_transport_user_name { String } 運搬担当者
 * @property manifest.report_vehicle_number { String } 車両番号
 * @property manifest.report_note { String } 運搬備考
 * @property manifest.target_for_registration { Boolean } 登録対象
 */
export default {
  name: "CompletionBasicInformation",
  components: {},
  mixins: [],
  props: {
    /**
     * マニフェスト
     */
    manifest: {
      type: Object,
      default: () => {},
    },
    totalManifestCount: {
      type: Number,
      default: () => 0,
    },
    /**
     * 呼び出し元
     */
    callerType: {
      type: String,
      default: () => null,
    },
    /**
     * 報告担当者リスト
     */
    users: {
      type: Array,
      default: () => {}
    },
    /**
     * 運搬担当者リスト
     */
    drivers: {
      type: Array,
      default: () => {}
    },
    /**
     * 車両リスト
     */
    vehicles: {
      type: Array,
      default: () => {}
    },
  },
  data() {
    return {
      systemDate: this.$dayjs().format("YYYY-MM-DD"),
    }
  },
  computed: {
    /**
     * 予約登録後365日経過判定
     */
    isExpiredReservation() {
      return this.manifest.manifest_type === 'dxe_form' && this.manifest.old_reservation;
    }
  },
  created() {
  },
  methods: {
    /**
     * 複写ボタン クリックイベント
     */
    copyFieldButtonClickEvent (property, value) {
      this.$emit("copyField", property, value);
    },
    /**
     * 基本情報複写ボタン クリックイベント
     */
    copyFullFieldButtonClickEvent () {
      this.$emit("copyFullField", this.manifest)
    },
    /**
     * カレンダーチェンジイベント コールバック
     * @param date
     */
    transportCompletedDateChangedCallback (date) {
      this.manifest.report_transport_completed_date = date;
    },
  },
}
</script>

<template>
  <div class="reportLayer">
    <v-row dense class="pb-3">
      <v-col cols="4">
        <h3>基本情報</h3>
      </v-col>
      <!-- エラーメッセージ（365日経過） -->
      <v-col
        v-if="isExpiredReservation"
        cols="8"
      >
        <v-alert
          type="error"
          dense
          outlined
          class="ma-0 error-message-small"
        >
          <span class="text-caption">予約登録後、365日経過している為、本登録出来ません</span>
        </v-alert>
      </v-col>
      <!-- /エラーメッセージ（365日経過） -->
      <v-col
        v-else-if="manifest.enable_copy"
        cols="8"
      >
        <v-btn
          :disabled="!manifest.target_for_registration"
          block
          color="primary"
          small
          @click="copyFullFieldButtonClickEvent"
        >
          基本情報複写
          &nbsp;<v-icon small>mdi-chevron-double-down</v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <v-row dense>
      <!-- 報告担当者 -->
      <v-col cols="6">
        <validation-provider
          v-slot="{ errors }"
          :disabled="!manifest.target_for_registration"
          :rules="{ max:12 }"
          :vid="`report_reporter_name-${manifest.id}`"
          name="報告担当者"
        >
          <v-combobox
            v-model="manifest.report_reporter_name"
            :disabled="!manifest.target_for_registration"
            :error-messages="errors"
            :items="users"
            :return-object="false"
            clearable
            dense
            label="報告担当者"
            hide-details="auto"
            outlined
            persistent-placeholder
          >
            <template v-slot:label>
              <span>報告担当者</span>
              <span class="ml-1">
                  ({{ manifest.report_reporter_name?.length || 0 }} / 12)
              </span>
            </template>
            <template v-slot:counter></template>
            <template v-slot:append-outer>
              <div
                v-if="callerType !== 'bulk'"
                class="copy-actions"
              >
                <v-btn
                  v-if="manifest.enable_copy"
                  :disabled="!manifest.target_for_registration"
                  color="primary"
                  small
                  @click.stop="copyFieldButtonClickEvent('report_reporter_name', manifest.report_reporter_name)"
                >
                  <v-icon>mdi-chevron-double-down</v-icon>
                </v-btn>
              </div>
            </template>
          </v-combobox>
        </validation-provider>
      </v-col>
      <!-- /報告担当者 -->

      <!-- 運搬終了日 -->
      <v-col cols="6">
        <cs-date-picker
          :date.sync="manifest.report_transport_completed_date"
          :disabled="!manifest.target_for_registration"
          :minimum-date="manifest.collected_date ? manifest.collected_date : null"
          :maximum-date="systemDate"
          @dateChangedCallback="transportCompletedDateChangedCallback"
        >
          <template v-slot="{ on, attrs }">
            <validation-provider
              v-slot="{ errors }"
              :disabled="!manifest.target_for_registration"
              :vid="`report_transport_completed_date-${manifest.id}`"
              class="requiredMark"
              name="運搬終了日"
              rules="required"
            >
              <v-text-field
                v-model="manifest.report_transport_completed_date"
                v-bind="attrs"
                v-on="on"
                :disabled="!manifest.target_for_registration"
                :error-messages="errors"
                append-icon="mdi-calendar"
                clearable
                dense
                hide-details="auto"
                label="運搬終了日"
                outlined
                persistent-hint
                persistent-placeholder
                readonly
              >
                <template v-slot:append-outer>
                  <div
                    v-if="callerType !== 'bulk'"
                    class="copy-actions"
                  >
                    <v-btn
                      v-if="manifest.enable_copy"
                      :disabled="!manifest.target_for_registration"
                      color="primary"
                      small
                      @click.stop="copyFieldButtonClickEvent('report_transport_completed_date', manifest.report_transport_completed_date)"
                    >
                      <v-icon>mdi-chevron-double-down</v-icon>
                    </v-btn>
                  </div>
                </template>
              </v-text-field>
            </validation-provider>
          </template>
        </cs-date-picker>
      </v-col>
      <!-- /運搬終了日 -->
    </v-row>
    <v-row dense>
      <!-- 運搬担当者 -->
      <v-col cols="6">
        <validation-provider
          v-slot="{ errors }"
          :vid="`report_transport_user_name-${manifest.id}`"
          :class="['dxe_form', 'transfer_form'].includes(manifest.manifest_type) ? 'requiredMark' : ''"
          :disabled="!manifest.target_for_registration"
          :rules="{ required: ['dxe_form', 'transfer_form'].includes(manifest.manifest_type), max: 12 }"
          name="運搬担当者"
        >
          <v-combobox
            v-model="manifest.report_transport_user_name"
            :disabled="!manifest.target_for_registration"
            :error-messages="errors"
            :items="drivers"
            :return-object="false"
            clearable
            dense
            label="運搬担当者"
            hide-details="auto"
            outlined
            persistent-placeholder
          >
            <template v-slot:label>
              <span>運搬担当者</span>
              <span class="ml-1">
                  ({{ manifest.report_transport_user_name?.length || 0 }} / 12)
                </span>
            </template>
            <template v-slot:counter></template>
            <template v-slot:append-outer>
              <div
                v-if="callerType !== 'bulk'"
                class="copy-actions"
              >
                <v-btn
                  v-if="manifest.enable_copy"
                  :disabled="!manifest.target_for_registration"
                  color="primary"
                  small
                  @click.stop="copyFieldButtonClickEvent('report_transport_user_name', manifest.report_transport_user_name)"
                >
                  <v-icon>mdi-chevron-double-down</v-icon>
                </v-btn>
              </div>

            </template>
          </v-combobox>
        </validation-provider>
      </v-col>
      <!-- /運搬担当者 -->

      <!-- 車両番号 -->
      <v-col cols="6">
        <validation-provider
          v-slot="{ errors }"
          :disabled="!manifest.target_for_registration"
          :vid="`report_vehicle_number-${manifest.id}`"
          name="車両番号"
          rules="max:15"
        >
          <v-combobox
            v-model="manifest.report_vehicle_number"
            :disabled="!manifest.target_for_registration"
            :error-messages="errors"
            :items="vehicles"
            :return-object="false"
            :clearable="true"
            dense
            hide-details="auto"
            item-text="number"
            item-value="number"
            label="車両番号"
            outlined
            persistent-placeholder
          >
            <template v-slot:label>
              <span>車両番号</span>
              <span class="ml-1">
                  ({{ manifest.report_vehicle_number?.length || 0 }} / 15)
                </span>
            </template>
            <template v-slot:counter></template>
            <template v-slot:append-outer>
              <div
                v-if="callerType !== 'bulk'"
                class="copy-actions"
              >
                <v-btn
                  v-if="manifest.enable_copy"
                  :disabled="!manifest.target_for_registration"
                  color="primary"
                  small
                  @click.stop="copyFieldButtonClickEvent('report_vehicle_number', manifest.report_vehicle_number)"
                >
                  <v-icon>mdi-chevron-double-down</v-icon>
                </v-btn>
              </div>
            </template>
          </v-combobox>
        </validation-provider>

      </v-col>
      <!-- /車両番号 -->
    </v-row>
    <v-row dense>
      <!-- 運搬備考 -->
      <v-col cols="12">
        <validation-provider
          v-slot="{ errors }"
          :disabled="!manifest.target_for_registration"
          :vid="`report_note-${manifest.id}`"
          name="運搬備考"
          rules="max:128"
        >
          <v-text-field
            v-model="manifest.report_note"
            :disabled="!manifest.target_for_registration"
            :error-messages="errors"
            dense
            hide-details="auto"
            label="運搬備考"
            outlined
            persistent-placeholder
          >
            <template v-slot:label>
              <span>運搬備考</span>
              <span class="ml-1">
                  ({{ manifest.report_note?.length || 0 }} / 128)
                </span>
            </template>
            <template v-slot:counter></template>
            <template v-slot:append-outer>
              <div
                v-if="callerType !== 'bulk'"
                class="copy-actions"
              >
                <v-btn
                  v-if="manifest.enable_copy"
                  :disabled="!manifest.target_for_registration"
                  color="primary"
                  small
                  @click.stop="copyFieldButtonClickEvent('report_note', manifest.report_note)"
                >
                  <v-icon>mdi-chevron-double-down</v-icon>
                </v-btn>
              </div>
            </template>
          </v-text-field>
        </validation-provider>
      </v-col>
      <!-- /運搬備考 -->
    </v-row>
  </div>
</template>

<style scoped lang="scss">
.error-message-small {
  font-size: 0.875rem;
  padding: 8px 12px !important;
}
</style>
